import type { AdventureToastNotification } from 'coop-adventure-engine';
import type { AdventureToastEvent } from '../types/websocket';

const TOAST_TYPES = new Set(['success', 'error', 'warning', 'info']);

export function stripHtmlFromToastMessage(message: string): string {
  if (!message) {
    return '';
  }
  return message.replace(/<[^>]*>/g, '').trim();
}

function normalizeToastType(type: unknown): 'success' | 'error' | 'warning' | 'info' {
  if (typeof type === 'string' && TOAST_TYPES.has(type)) {
    return type as 'success' | 'error' | 'warning' | 'info';
  }
  return 'info';
}

export function getPlayerCharacterName(
  player: { characterName?: string; currentCharacter?: string } | null | undefined
): string | undefined {
  return player?.currentCharacter || player?.characterName;
}

export type ShowToastFn = (
  message: string,
  type: 'success' | 'error' | 'warning' | 'info',
  duration: number
) => void;

/** Show REST response toasts only for the current player's character (fallback when WS is down). */
export function showAdventureToastsForPlayer(
  toasts: AdventureToastNotification[] | undefined,
  playerCharacterName: string | undefined,
  showToast: ShowToastFn
): void {
  if (!toasts?.length || !playerCharacterName) {
    return;
  }
  const normalizedPlayer = playerCharacterName.trim();
  for (const toast of toasts) {
    const target = (toast.characterName ?? '').trim();
    if (!target || target !== normalizedPlayer) {
      continue;
    }
    const message = stripHtmlFromToastMessage(toast.message ?? '');
    if (!message) {
      continue;
    }
    showToast(message, normalizeToastType(toast.type), toast.durationMs ?? 5000);
  }
}

/** Handle `adventure-toast` WebSocket events for the connected player. */
export function handleAdventureToastSocketEvent(
  data: AdventureToastEvent,
  context: {
    sessionId: string | undefined;
    playerCharacterName: string | undefined;
    showToast: ShowToastFn;
  }
): void {
  if (!data?.message) {
    return;
  }
  if (context.sessionId && data.sessionId && data.sessionId !== context.sessionId) {
    return;
  }
  const target = (data.characterName ?? '').trim();
  const playerChar = (context.playerCharacterName ?? '').trim();
  if (!target || !playerChar || target !== playerChar) {
    return;
  }
  const message = stripHtmlFromToastMessage(data.message);
  if (!message) {
    return;
  }
  context.showToast(message, normalizeToastType(data.type), data.durationMs ?? 5000);
}
